import { CustomerShipmentDetails } from "./customer-shipment-details";
import { useState } from "react";
import { useParams, Link } from "react-router";
import { Button, Card, CardBody, Chip, Skeleton } from "@heroui/react";
import { ArrowLeft, Package } from "lucide-react";
import { Button as DialogButton } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useCustomerOrdersV2,
  getOrderStatus,
} from "@/api/use-customer-orders-v2";
import { ShipmentStatusBadge } from "@/components/shipment-status-badge";
import { useTranslation } from "react-i18next";
import { useOrderPayments } from "@/api/use-order-payments";
import { useRequestCancellation } from "@/api/use-request-cancellation";
import { toast } from "@/hooks/use-toast";
import { PayOrderDialog } from "./pay-order-dialog";
import { CancellationPendingNotice } from "./cancellation-pending-notice";
import { canRequestCustomerShipmentCancellation } from "@/api/shipment-action-eligibility";
import {
  getReturnAmountDue,
  ReturnRefundBreakdown,
} from "./return-refund-breakdown";

import { hasOrderRefund, OrderPaymentSummary } from "./order-payment-summary";

function formatCurrency(cents?: number): string {
  if (cents === undefined || cents === null) return "—";
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(cents / 100);
}

export default function OrderDetailsScreen() {
  const { t, i18n } = useTranslation("app");
  const { orderId } = useParams<{ orderId: string }>();
  const [returnTransportToPay, setReturnTransportToPay] = useState<
    string | null
  >(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const { data: response, isLoading } = useCustomerOrdersV2({
    page: 1,
    limit: 100,
  });
  const { data: payments = [] } = useOrderPayments(orderId);
  const requestCancellation = useRequestCancellation({
    onSuccess: (data) => {
      if (!data.success) {
        toast({
          variant: "destructive",
          title: t("orders.toastCancellationError"),
          description: data.message,
        });
        return;
      }
      toast({
        title: t("orders.toastCancellationTitle"),
        description: data.message,
      });
      setCancelDialogOpen(false);
    },
    onError: () =>
      toast({
        variant: "destructive",
        title: t("orders.toastCancellationError"),
      }),
  });

  const order = response?.data?.find((o) => o.id === orderId);

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto space-y-4">
        <Skeleton className="h-8 w-32 rounded-medium" />
        <Card shadow="none" className="border border-divider">
          <CardBody className="p-6 space-y-4">
            <Skeleton className="h-6 w-48 rounded-medium" />
            <Skeleton className="h-4 w-full rounded-medium" />
            <Skeleton className="h-4 w-3/4 rounded-medium" />
          </CardBody>
        </Card>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="max-w-4xl mx-auto">
        <Card shadow="none" className="border border-divider">
          <CardBody className="py-12 text-center">
            <Package className="h-12 w-12 text-default-300 mx-auto mb-3" />
            <p className="font-medium text-default-700">
              {t("orderDetails.notFoundTitle")}
            </p>
            <p className="text-small text-default-500 mt-1">
              {t("orderDetails.notFoundBody")}
            </p>
            <Button
              as={Link}
              to="/orders"
              variant="bordered"
              size="sm"
              startContent={<ArrowLeft className="h-4 w-4" />}
              className="mt-4"
            >
              {t("orderDetails.backToOrders")}
            </Button>
          </CardBody>
        </Card>
      </div>
    );
  }

  const status = getOrderStatus(order);
  const shipments = order.shipments ?? [];
  const shipment = shipments[0];
  const isPaid = order.paymentStatus === "PAID";
  const hasTax = order.taxCents != null && order.taxCents > 0;
  const hasPendingCancellation = order.shipments?.some(
    (item) => item.cancellationRequestedAt
  );
  const cancellationShipments =
    order.shipments?.filter((item) =>
      canRequestCustomerShipmentCancellation(item.status)
    ) ?? [];
  const orderReference = `ORD-${order.orderNumber}`;
  const showCancel =
    !hasPendingCancellation && cancellationShipments.length > 0;
  const shipmentReturns =
    order.shipments?.flatMap((item) =>
      (item.returnTransports ?? []).map((transport) => ({
        transport,
        shipmentReference: item.shipmentNumber ?? item.id,
      }))
    ) ?? [];
  const returnTransports = shipmentReturns.map((item) => item.transport);
  const selectedReturnTransport = returnTransports.find(
    (transport) => transport.id === returnTransportToPay
  );
  const amountDueToPay = selectedReturnTransport
    ? getReturnAmountDue(selectedReturnTransport, payments)
    : 0;

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      <Link
        to="/orders"
        className="inline-flex items-center gap-1 text-small text-default-500 hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        {t("orderDetails.backToOrders")}
      </Link>

      {hasPendingCancellation && <CancellationPendingNotice />}

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-2">
          <h1 className="text-2xl font-bold tracking-tight">
            {shipments.length > 1
              ? orderReference
              : `SHP-${shipment?.shipmentNumber ?? "…"}`}
          </h1>
          <p className="text-small text-default-500">
            {new Date(order.createdAt).toLocaleDateString(
              i18n.language === "zh" ? "zh-CN" : "en-CA",
              {
                weekday: "long",
                month: "short",
                day: "numeric",
                year: "numeric",
              }
            )}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            {shipments.length === 1 ? (
              <ShipmentStatusBadge
                status={status}
                paymentStatus={order.paymentStatus}
              />
            ) : (
              <Chip variant="flat" size="sm">
                {t("orderDetails.shipmentsCount", { count: shipments.length })}
              </Chip>
            )}
            {isPaid && (
              <Chip color="success" variant="flat" size="sm">
                {t("orderDetails.paid")}
              </Chip>
            )}
          </div>
        </div>
        {showCancel && (
          <div className="ml-auto flex max-w-full flex-col items-end gap-1">
            <DialogButton
              size="sm"
              variant="outline"
              className="max-w-full whitespace-normal text-right text-destructive hover:text-destructive"
              onClick={() => setCancelDialogOpen(true)}
              aria-describedby="order-cancellation-scope"
            >
              {t("orderDetails.requestOrderCancellation", { orderReference })}
            </DialogButton>
            <p
              id="order-cancellation-scope"
              className="text-right text-tiny text-default-500"
            >
              {t("orderDetails.cancellationScope", {
                count: cancellationShipments.length,
              })}
            </p>
          </div>
        )}
      </div>

      <Card shadow="none" className="border border-divider">
        <CardBody className="py-4 space-y-1">
          {hasTax && (
            <>
              <div className="flex justify-between items-center text-small text-default-500">
                <span>{t("orderDetails.subtotal")}</span>
                <span>
                  {formatCurrency(order.subtotalCents ?? order.totalCents)}
                </span>
              </div>
              <div className="flex justify-between items-center text-small text-default-500">
                <span>{t("orderDetails.tax")}</span>
                <span>{formatCurrency(order.taxCents)}</span>
              </div>
            </>
          )}
          <div className="flex justify-between items-center pt-1">
            <span className="text-default-500">
              {t(
                hasOrderRefund(order)
                  ? "orders.refundSummary.orderTotal"
                  : "orderDetails.total"
              )}
            </span>
            <span className="text-2xl font-bold tracking-tight">
              {formatCurrency(order.totalCents)}
            </span>
          </div>
          <OrderPaymentSummary order={order} showBreakdown />
        </CardBody>
      </Card>

      {shipmentReturns.map(({ transport, shipmentReference }) => (
        <ReturnRefundBreakdown
          key={transport.id}
          transport={transport}
          shipmentReference={shipmentReference}
          payments={payments}
          onPayAmountDue={() => setReturnTransportToPay(transport.id)}
        />
      ))}

      {order.shipments?.map((item) => (
        <CustomerShipmentDetails
          key={item.id}
          shipment={item}
          showHeading={shipments.length > 1}
        />
      ))}
      {amountDueToPay > 0 && (
        <PayOrderDialog
          open
          onOpenChange={(open) => !open && setReturnTransportToPay(null)}
          orderId={order.id}
          totalCents={amountDueToPay}
          orderNumber={`ORD-${order.orderNumber}`}
          allowInvoice={false}
          returnTransport={selectedReturnTransport}
          purpose="RETURN"
        />
      )}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("orderDetails.cancellationTitle", { orderReference })}
            </DialogTitle>
            <DialogDescription>
              {t("orderDetails.cancellationBody", { orderReference })}
            </DialogDescription>
            <ul className="list-disc pl-5 text-sm">
              {cancellationShipments.map((item) => (
                <li key={item.id}>SHP-{item.shipmentNumber ?? "…"}</li>
              ))}
            </ul>
          </DialogHeader>
          <DialogFooter>
            <DialogButton
              variant="outline"
              onClick={() => setCancelDialogOpen(false)}
            >
              {t("orders.keepOrder")}
            </DialogButton>
            <DialogButton
              variant="destructive"
              onClick={() => requestCancellation.mutate({ orderId: order.id })}
              disabled={requestCancellation.isPending}
              aria-busy={requestCancellation.isPending}
            >
              {requestCancellation.isPending
                ? t("orders.submitting")
                : t("orders.requestCancellation")}
            </DialogButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
